import { Type } from "@google/genai";

export const DOCUMENT_ANALYSIS_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    filename: { type: Type.STRING },
    detected_document_type: { type: Type.STRING },
    classification_confidence: { type: Type.NUMBER },
    document_retention: {
      type: Type.OBJECT,
      properties: {
        keep_visible_on_documents_page: { type: Type.BOOLEAN }
      }
    },
    document_summary: { type: Type.STRING },
    global_fields: {
      type: Type.OBJECT,
      properties: {
        project_title: { type: Type.STRING, nullable: true },
        show_title: { type: Type.STRING, nullable: true },
        series_title: { type: Type.STRING, nullable: true },
        production_company: { type: Type.STRING, nullable: true },
        client: { type: Type.STRING, nullable: true },
        vendor: { type: Type.STRING, nullable: true },
        episode_identifiers: { type: Type.ARRAY, items: { type: Type.STRING } },
        date_mentions: { type: Type.ARRAY, items: { type: Type.STRING } },
        people_mentions: { type: Type.ARRAY, items: { type: Type.STRING } },
        key_terms: { type: Type.ARRAY, items: { type: Type.STRING } },
        source_sheets_or_sections: { type: Type.ARRAY, items: { type: Type.STRING } },
        raw_tables_detected: { type: Type.ARRAY, items: { type: Type.STRING } },
        possible_date_formats: { type: Type.ARRAY, items: { type: Type.STRING } },
        notes: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    },
    script_data: {
      type: Type.OBJECT,
      nullable: true,
      properties: {
        series_title: { type: Type.STRING, nullable: true },
        episode_number: { type: Type.STRING, nullable: true },
        episode_title: { type: Type.STRING, nullable: true },
        script_draft: { type: Type.STRING, nullable: true },
        revision_date: { type: Type.STRING, nullable: true },
        writer: { type: Type.STRING, nullable: true },
        total_pages: { type: Type.NUMBER, nullable: true },
        scene_count: { type: Type.NUMBER, nullable: true },
        scenes: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              scene_number: { type: Type.STRING, nullable: true },
              heading: { type: Type.STRING, nullable: true },
              script_text: { type: Type.STRING, nullable: true },
              description: { type: Type.STRING, nullable: true },
              location_name: { type: Type.STRING, nullable: true },
              int_ext: { type: Type.STRING, nullable: true },
              day_night: { type: Type.STRING, nullable: true },
              script_page_range: { type: Type.STRING, nullable: true },
              characters: { type: Type.ARRAY, items: { type: Type.STRING } },
              inferred_vfx_requirements: { type: Type.ARRAY, items: { type: Type.STRING } },
              estimated_complexity: { type: Type.STRING, nullable: true }
            }
          }
        },
        scene_headings: { type: Type.ARRAY, items: { type: Type.STRING } },
        locations: { type: Type.ARRAY, items: { type: Type.STRING } },
        characters: { type: Type.ARRAY, items: { type: Type.STRING } },
        characters_by_scene: { type: Type.ARRAY, items: { type: Type.STRING } },
        vfx_indicators: { type: Type.ARRAY, items: { type: Type.STRING } },
        special_requirements: { type: Type.ARRAY, items: { type: Type.STRING } },
        scene_analysis: { type: Type.ARRAY, items: { type: Type.STRING } },
        script_summary: { type: Type.STRING, nullable: true }
      }
    },
    legacy_bid_csv_data: {
      type: Type.OBJECT,
      nullable: true,
      properties: {
        estimate_name: { type: Type.STRING, nullable: true },
        estimate_date: { type: Type.STRING, nullable: true },
        bid_version: { type: Type.STRING, nullable: true },
        project_name: { type: Type.STRING, nullable: true },
        show_name: { type: Type.STRING, nullable: true },
        currency: { type: Type.STRING, nullable: true },
        totals: {
          type: Type.OBJECT,
          properties: {
            shot_total: { type: Type.NUMBER, nullable: true },
            asset_total: { type: Type.NUMBER, nullable: true },
            frame_total: { type: Type.NUMBER, nullable: true },
            grand_total: { type: Type.NUMBER, nullable: true }
          }
        },
        line_items: { 
          type: Type.ARRAY, 
          items: { 
            type: Type.OBJECT,
            description: "A list of line items (shots, assets, tasks) found in the document. Each object should contain the column headers as keys and the row values as values.",
          } 
        },
        aggregates: {
          type: Type.OBJECT,
          properties: {
            highest_cost_shots: { type: Type.ARRAY, items: { type: Type.STRING } },
            highest_cost_assets: { type: Type.ARRAY, items: { type: Type.STRING } },
            most_frequent_task_types: { type: Type.ARRAY, items: { type: Type.STRING } },
            cost_drivers: { type: Type.ARRAY, items: { type: Type.STRING } },
            duplicates_or_anomalies: { type: Type.ARRAY, items: { type: Type.STRING } }
          }
        }
      }
    },
    post_schedule_data: {
      type: Type.OBJECT,
      nullable: true,
      properties: {
        series_title: { type: Type.STRING, nullable: true },
        show_title: { type: Type.STRING, nullable: true },
        schedule_name: { type: Type.STRING, nullable: true },
        schedule_type: { type: Type.STRING, nullable: true },
        overall_schedule_start: { type: Type.STRING, nullable: true },
        overall_schedule_end: { type: Type.STRING, nullable: true },
        overall_first_lock_date: { type: Type.STRING, nullable: true },
        overall_last_vfx_final_delivery_date: { type: Type.STRING, nullable: true },
        format_notes: { type: Type.ARRAY, items: { type: Type.STRING } },
        runtime_notes: { type: Type.ARRAY, items: { type: Type.STRING } },
        delivery_partners: { type: Type.ARRAY, items: { type: Type.STRING } },
        broadcaster_names: { type: Type.ARRAY, items: { type: Type.STRING } },
        source_sheet_names: { type: Type.ARRAY, items: { type: Type.STRING } },
        global_milestones: { type: Type.ARRAY, items: { type: Type.STRING } },
        episodes: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              episode_number: { type: Type.STRING, nullable: true },
              episode_code: { type: Type.STRING, nullable: true },
              episode_title: { type: Type.STRING, nullable: true },
              runtime_version: { type: Type.STRING, nullable: true },
              delivery_version: { type: Type.STRING, nullable: true },
              milestones: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    milestone_type: { type: Type.STRING, nullable: true },
                    milestone_subtype: { type: Type.STRING, nullable: true },
                    normalized_date: { type: Type.STRING, nullable: true },
                    original_date_text: { type: Type.STRING, nullable: true },
                    original_milestone_text: { type: Type.STRING, nullable: true },
                    source_sheet: { type: Type.STRING, nullable: true },
                    source_cell_or_section: { type: Type.STRING, nullable: true },
                    confidence: { type: Type.NUMBER, nullable: true },
                    notes: { type: Type.STRING, nullable: true }
                  }
                }
              },
              delivery_summary: {
                type: Type.OBJECT,
                properties: {
                  cutting_copy_date: { type: Type.STRING, nullable: true },
                  proposed_lock_date: { type: Type.STRING, nullable: true },
                  pre_lock_date: { type: Type.STRING, nullable: true },
                  hard_lock_date: { type: Type.STRING, nullable: true },
                  lock_date: { type: Type.STRING, nullable: true },
                  picture_lock_date: { type: Type.STRING, nullable: true },
                  editorial_lock_date: { type: Type.STRING, nullable: true },
                  vfx_turnover_date: { type: Type.STRING, nullable: true },
                  temp_vfx_delivery_date: { type: Type.STRING, nullable: true },
                  final_vfx_delivery_date: { type: Type.STRING, nullable: true },
                  final_delivery_date: { type: Type.STRING, nullable: true },
                  bbc_delivery_date: { type: Type.STRING, nullable: true },
                  freemantle_delivery_date: { type: Type.STRING, nullable: true }
                }
              },
              notes: { type: Type.ARRAY, items: { type: Type.STRING } }
            }
          }
        },
        cadence_observations: { type: Type.ARRAY, items: { type: Type.STRING } },
        overlaps_between_episodes: { type: Type.ARRAY, items: { type: Type.STRING } },
        compressed_delivery_windows: { type: Type.ARRAY, items: { type: Type.STRING } },
        likely_schedule_risks: { type: Type.ARRAY, items: { type: Type.STRING } },
        missing_episode_dates: { type: Type.ARRAY, items: { type: Type.STRING } },
        important_dependencies: { type: Type.ARRAY, items: { type: Type.STRING } },
        overall_notes: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    },
    cross_document_insights: {
      type: Type.OBJECT,
      properties: {
        useful_for_bidding: { type: Type.ARRAY, items: { type: Type.STRING } },
        useful_for_scheduling: { type: Type.ARRAY, items: { type: Type.STRING } },
        useful_for_tracking: { type: Type.ARRAY, items: { type: Type.STRING } },
        risks: { type: Type.ARRAY, items: { type: Type.STRING } },
        recommended_follow_up_fields: { type: Type.ARRAY, items: { type: Type.STRING } }
      }
    },
    uncertainties: { type: Type.ARRAY, items: { type: Type.STRING } },
    top_sheet_data: {
      type: Type.OBJECT,
      nullable: true,
      properties: {
        quote_name: { type: Type.STRING, nullable: true },
        quote_date: { type: Type.STRING, nullable: true },
        version: { type: Type.STRING, nullable: true },
        costs: {
          type: Type.OBJECT,
          nullable: true,
          properties: {
            shot_total: { type: Type.NUMBER, nullable: true },
            asset_total: { type: Type.NUMBER, nullable: true },
            onset_total: { type: Type.NUMBER, nullable: true },
            grand_total: { type: Type.NUMBER, nullable: true },
            shot_count: { type: Type.NUMBER, nullable: true },
            asset_count: { type: Type.NUMBER, nullable: true },
            currency: { type: Type.STRING, nullable: true }
          }
        },
        summary: { type: Type.STRING, nullable: true },
        approach: { type: Type.STRING, nullable: true },
        assumptions: { type: Type.STRING, nullable: true },
        clarifications: { type: Type.STRING, nullable: true },
        exclusions: { type: Type.STRING, nullable: true },
        estimate_assumptions: { type: Type.STRING, nullable: true },
        terms_and_conditions: { type: Type.STRING, nullable: true }
      }
    },
    quote_estimate_data: {
      type: Type.OBJECT,
      nullable: true,
      properties: {
        shot_rates: {
          type: Type.OBJECT,
          description: "Shot Rates extracted from the Key page columns H1:V2. Keys are the rate names, values are the numerical rates.",
          nullable: true
        }
      }
    }
  }
};
